import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ElementType,
} from 'react';

import { cx } from '../internal/cx.js';
import { buildSpacingStyle, pickSpacingProps, type SpacingProps } from '../internal/spacing.js';

export type GridFlow = 'row' | 'column' | 'dense' | 'row-dense' | 'column-dense';

export type GridProps = ComponentPropsWithoutRef<'div'> &
  SpacingProps & {
    /** Polymorphic element tag. */
    as?: ElementType;
    columns?: number | string;
    rows?: number | string;
    flow?: GridFlow;
  };

/** CSS grid layout primitive. Requires `@import '@nrx/ui/theme.css'`. */
export const Grid = forwardRef<HTMLElement, GridProps>(function Grid(
  {
    as,
    className,
    style,
    columns,
    rows,
    flow,
    gap,
    gapX,
    gapY,
    p,
    px,
    py,
    pt,
    pr,
    pb,
    pl,
    m,
    mx,
    my,
    mt,
    mr,
    mb,
    ml,
    ...rest
  },
  ref,
): React.JSX.Element {
  const Component = as ?? 'div';
  const spacingStyle = buildSpacingStyle(
    pickSpacingProps({
      gap,
      gapX,
      gapY,
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
    }),
  );

  const gridStyle: CSSProperties = { ...spacingStyle, ...style };
  if (columns !== undefined) {
    gridStyle.gridTemplateColumns =
      typeof columns === 'number' ? `repeat(${columns}, minmax(0, 1fr))` : columns;
  }
  if (rows !== undefined) {
    gridStyle.gridTemplateRows =
      typeof rows === 'number' ? `repeat(${rows}, minmax(0, 1fr))` : rows;
  }
  if (flow !== undefined) {
    gridStyle.gridAutoFlow = flow;
  }

  return (
    <Component
      ref={ref as never}
      className={cx('nrx-grid', className)}
      style={gridStyle}
      {...rest}
    />
  );
});
